import useMobileCheck from "./useMobileCheck";
import { useUserPreference } from "./useUserPreference";

/** Tailwind-safe grid class names so JIT includes them */
const GRID_COLS_CLASSES = {
	1: "grid-cols-1",
	2: "grid-cols-2",
	3: "grid-cols-3",
	4: "grid-cols-4",
	5: "grid-cols-5",
	6: "grid-cols-6",
};

/**
 * Shared responsive grid column count based on viewport width.
 * Use this instead of duplicating windowSize/isNarrow/gridSize logic.
 *
 * @param {Object} options
 * @param {number} [options.defaultCols=3] - Default columns when viewport is between narrow and wide (e.g. 1000–2000px).
 * @param {boolean} [options.considerSidebar=false] - If true, adds +1 column when sidepane and sidebar are both closed (more space).
 * @returns {{ gridSize: number, gridClassName: string, isNarrow: boolean }}
 */
function useResponsiveGridSize({ defaultCols = 3, considerSidebar = false } = {}) {
	const { isMobile, windowSize } = useMobileCheck();
	const { sidePane, sidebar } = useUserPreference();

	const isNarrow = typeof windowSize === "number" ? windowSize < 1000 : false;

	let gridSize = defaultCols;

	if (isMobile || isNarrow) {
		gridSize = 1;
	} else if (typeof windowSize === "number" && windowSize > 2000) {
		if (windowSize > 4000) {
			gridSize = 6;
		} else if (windowSize > 3000) {
			gridSize = 5;
		} else {
			gridSize = 4;
		}
	}

	if (considerSidebar && !(isMobile || isNarrow)) {
		gridSize = sidePane && sidebar ? gridSize : gridSize + 1;
	}

	gridSize = Math.min(6, Math.max(1, gridSize));
	const gridClassName = GRID_COLS_CLASSES[gridSize] ?? GRID_COLS_CLASSES[defaultCols];

	return { gridSize, gridClassName, isNarrow };
}

export default useResponsiveGridSize;
export { GRID_COLS_CLASSES };
